import { format } from "date-fns";
import { Copy, UserCheck } from "lucide-react";
import { ChangeEvent, useMemo, useRef, useState } from "react";

import {
  SUPPORT_REASON_LABEL,
  SUPPORT_STATUS_BADGE_CLASSNAME,
  SUPPORT_STATUS_LABEL,
  SupportRequest,
  SupportRequestAttachment,
  SupportRequestReason,
  SupportRequestStatusEnum,
} from "@/app/entities/SupportRequest";
import { useAuth } from "@/app/hooks/useAuth";
import { SupportTeamMember } from "@/app/services/supportRequestService/getSupportTeam";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { cn } from "@/app/utils/cn";
import { buildChatItems } from "@/app/utils/supportChat";
import {
  allowedTransitions,
  updateNotAllowed,
  validateSupportFiles,
} from "@/app/utils/supportRequest";
import { AttachmentViewer } from "@/view/components/AttachmentViewer";
import { Badge } from "@/view/components/ui/badge";
import { Button } from "@/view/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/view/components/ui/select";
import { toast } from "@/view/components/ui/use-toast";

import { ChatClosedNotice } from "../../SupportRequests/components/SupportRequestDetailsSheet/chat/ChatClosedNotice";
import { ChatComposer } from "../../SupportRequests/components/SupportRequestDetailsSheet/chat/ChatComposer";
import { SupportRequestChat } from "../../SupportRequests/components/SupportRequestDetailsSheet/chat/SupportRequestChat";
import { resolveRequester } from "../resolveRequester";
import { SupportRequestStatusBar } from "./SupportRequestStatusBar";
import { SupportRequestContext } from "./SupportRequestContext";

interface SupportRequestDetailsProps {
  data: SupportRequest;
  comment: string;
  setComment: (value: string) => void;
  files: File[];
  setFiles: (files: File[]) => void;
  handleSaveComment: () => void;
  handleUpdateStatus: (to: SupportRequestStatusEnum) => void;
  onOpenTransaction?: (transactionId: string) => void;
  onOpenSeller?: (sellerId: string) => void;
  isPending?: boolean;
  supportTeam: SupportTeamMember[];
  onAssign: (userId?: string) => void;
  isAssigning?: boolean;
  productsCatalog?: ProductCatalogItem[];
  readOnly?: boolean;
}

export const SupportRequestDetails = ({
  data,
  comment,
  setComment,
  files,
  setFiles,
  handleSaveComment,
  handleUpdateStatus,
  onOpenTransaction,
  onOpenSeller,
  isPending,
  supportTeam,
  onAssign,
  isAssigning,
  productsCatalog,
  readOnly,
}: SupportRequestDetailsProps) => {
  const { user } = useAuth();
  const [viewerAttachment, setViewerAttachment] =
    useState<SupportRequestAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    number,
    status,
    reason,
    resolution,
    resolvedAt,
    assignedTo,
    updatedAt,
    createdAt,
    product,
  } = data;

  const canComment = !readOnly && !updateNotAllowed.includes(status);
  const nextStatuses = allowedTransitions[status] ?? [];
  const requester = resolveRequester(data);
  const isAssignedToOther = !!assignedTo?.id && assignedTo.id !== user?._id;

  // mesma conversa do vendedor; aqui quem vê é sempre a equipe de suporte
  const chatItems = useMemo(
    () =>
      buildChatItems({
        supportRequest: data,
        currentUserId: user?._id,
        isSupportStaff: true,
      }),
    [data, user?._id],
  );

  const canSubmit = (!!comment.trim() || files.length > 0) && !isPending;

  const handleSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;

    const { accepted, error } = validateSupportFiles(files, selected);
    if (error) toast({ variant: "destructive", title: error });
    if (accepted.length) setFiles([...files, ...accepted]);
  };

  const productLabel = product
    ? (productsCatalog?.find((item) => item.key === product)?.label ?? product)
    : undefined;

  // chamado direcionado ao WhatsApp não tem motivo escolhido pelo vendedor
  const isWhatsappDirect = reason === SupportRequestReason.WHATSAPP_DIRECT;
  const title = isWhatsappDirect ? "WhatsApp" : SUPPORT_REASON_LABEL[reason];

  const handleCopyNumber = async () => {
    if (!number) return;
    await navigator.clipboard.writeText(number);
    toast({ title: "Código do chamado copiado" });
  };

  return (
    <>
      <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-start">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-2xl font-bold text-black01">{title}</h3>
            <Badge
              className={cn(
                "w-fit whitespace-nowrap border-transparent",
                SUPPORT_STATUS_BADGE_CLASSNAME[status],
              )}
            >
              {SUPPORT_STATUS_LABEL[status]}
            </Badge>
          </div>
          <p className="flex flex-wrap items-center gap-2 text-sm text-black04">
            <button
              type="button"
              title="Copiar código do chamado"
              disabled={!number}
              onClick={handleCopyNumber}
              className="flex items-center gap-1.5 rounded-md border border-gray01 bg-white px-2 py-0.5 font-semibold text-black03 transition-colors hover:border-black04 hover:bg-gray-50 disabled:pointer-events-none"
            >
              #{number || "-"}
              {!!number && <Copy className="h-3.5 w-3.5 text-black04" />}
            </button>
            {createdAt && (
              <span>
                Aberto em {format(new Date(createdAt), "dd/MM/yyyy 'às' HH:mm")}
              </span>
            )}
            {updatedAt && (
              <span>
                · Atualizado em{" "}
                {format(new Date(updatedAt), "dd/MM/yyyy 'às' HH:mm")}
              </span>
            )}
            {resolvedAt && (
              <span>
                · Resolvido em{" "}
                {format(new Date(resolvedAt), "dd/MM/yyyy 'às' HH:mm")}
              </span>
            )}
            {requester.name && <span>· aberto por {requester.name}</span>}
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-xl border border-gray01 bg-white px-3 py-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-100 text-sm font-semibold text-orange-700">
            {(assignedTo?.name || "?")
              .split(" ")
              .map((part) => part[0])
              .slice(0, 2)
              .join("")
              .toUpperCase()}
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase text-black04">
              Responsável
            </span>
            {canComment ? (
              <Select
                value={assignedTo?.id ?? ""}
                disabled={isAssigning}
                onValueChange={onAssign}
              >
                <SelectTrigger className="h-6 w-[180px] border-0 p-0 text-base font-bold shadow-none focus:ring-0">
                  <SelectValue placeholder="Sem responsável" />
                </SelectTrigger>
                <SelectContent>
                  {supportTeam.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-base font-bold text-black01">
                {assignedTo?.name || "-"}
              </span>
            )}
          </div>
        </div>
      </div>

      <SupportRequestContext
        data={data}
        productLabel={productLabel}
        onOpenTransaction={onOpenTransaction}
        onOpenSeller={onOpenSeller}
      />

      {!!resolution && (
        <div className="rounded-xl border border-gray01 bg-gray-50 p-4">
          <p className="pb-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Resolução
          </p>
          <p className="whitespace-break-spaces text-sm text-black03">
            {resolution}
          </p>
        </div>
      )}

      {readOnly ? null : isAssignedToOther && canComment ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray01 bg-white p-3">
          <Button
            size="sm"
            disabled={isAssigning}
            onClick={() => onAssign(undefined)}
            className="gap-2 bg-black01 text-white hover:bg-black01/90"
          >
            <UserCheck className="h-4 w-4" />
            Assumir chamado
          </Button>
          <span className="text-sm text-black04">
            Este chamado está com {assignedTo?.name}.
          </span>
        </div>
      ) : (
        <SupportRequestStatusBar
          availableTransitions={nextStatuses}
          disabled={isPending || !canComment}
          onSelect={handleUpdateStatus}
        />
      )}

      <div className="flex flex-col gap-2 border-t border-gray01 pt-4">
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Conversa
        </p>
        <div className="flex h-[560px] flex-col overflow-hidden rounded-lg border border-gray-200">
          <SupportRequestChat
            items={chatItems}
            onOpenAttachment={setViewerAttachment}
          />

          {canComment ? (
            <ChatComposer
              message={comment}
              setMessage={setComment}
              files={files}
              fileInputRef={fileInputRef}
              onSelectFiles={handleSelectFiles}
              onRemoveFile={(index) =>
                setFiles(files.filter((_, i) => i !== index))
              }
              onOpenFilePicker={() => fileInputRef.current?.click()}
              onSend={handleSaveComment}
              allowAttachments
              canSubmit={canSubmit}
              isSending={!!isPending}
            />
          ) : (
            updateNotAllowed.includes(status) && <ChatClosedNotice />
          )}
        </div>
      </div>

      <AttachmentViewer
        attachment={viewerAttachment}
        onClose={() => setViewerAttachment(null)}
      />
    </>
  );
};
